import { useTranslation } from 'react-i18next';
import { InstagramIcon, FacebookIcon } from '../icons/BrandIcons';
import { SOCIAL } from '../../config/social';

/**
 * Reusable Instagram + Facebook links, official brand colors, accessible, tracked.
 *
 * - `variant="icon"`  → round/square colored icon buttons (footer, mobile menu).
 * - `variant="pill"`  → full pill buttons with label (contact page, CTAs).
 * - `tone="onDark"`   → icon-variant on dark backgrounds (white chips, brand color on hover).
 */

type Variant = 'icon' | 'pill';
type Size = 'sm' | 'md' | 'lg';
type Tone = 'color' | 'onDark';

interface SocialLinksProps {
    variant?: Variant;
    size?: Size;
    tone?: Tone;
    className?: string;
}

const ICON_PX: Record<Size, number> = { sm: 18, md: 22, lg: 26 };
const BTN_SIZE: Record<Size, string> = {
    sm: 'h-10 w-10',
    md: 'h-11 w-11',
    lg: 'h-12 w-12',
};

const trackClick = (network: string) => {
    window.gtag?.('event', 'social_click', { network });
};

export function SocialLinks({ variant = 'icon', size = 'md', tone = 'color', className = '' }: SocialLinksProps) {
    const { t } = useTranslation();
    const iconPx = ICON_PX[size];

    if (variant === 'pill') {
        return (
            <div className={`flex flex-wrap items-center gap-3 ${className}`}>
                <a
                    href={SOCIAL.instagram.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('social.ariaInstagram')}
                    onClick={() => trackClick('instagram')}
                    className="inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #FEDA75 0%, #FA7E1E 25%, #D62976 50%, #962FBF 75%, #4F5BD5 100%)' }}
                >
                    <InstagramIcon size={iconPx} title="" />
                    {t('social.instagram')}
                </a>
                <a
                    href={SOCIAL.facebook.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('social.ariaFacebook')}
                    onClick={() => trackClick('facebook')}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#1877F2] px-4 py-3 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#0f63d6] hover:shadow-lg"
                >
                    <FacebookIcon size={iconPx} title="" />
                    {t('social.facebook')}
                </a>
            </div>
        );
    }

    // variant === 'icon'
    const base = `inline-flex items-center justify-center rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg ${BTN_SIZE[size]}`;

    if (tone === 'onDark') {
        // White chip background, brand color revealed on the glyph (and full brand on hover).
        return (
            <div className={`flex items-center gap-3 ${className}`}>
                <a
                    href={SOCIAL.instagram.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('social.ariaInstagram')}
                    onClick={() => trackClick('instagram')}
                    className={`${base} bg-white/10 text-white hover:bg-white hover:text-[#D62976]`}
                >
                    <InstagramIcon size={iconPx} title="" />
                </a>
                <a
                    href={SOCIAL.facebook.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t('social.ariaFacebook')}
                    onClick={() => trackClick('facebook')}
                    className={`${base} bg-white/10 text-white hover:bg-white hover:text-[#1877F2]`}
                >
                    <FacebookIcon size={iconPx} title="" />
                </a>
            </div>
        );
    }

    // tone === 'color' (light backgrounds): full official brand colors.
    return (
        <div className={`flex items-center gap-3 ${className}`}>
            <a
                href={SOCIAL.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('social.ariaInstagram')}
                onClick={() => trackClick('instagram')}
                className={`${base} text-white`}
                style={{ background: 'linear-gradient(135deg, #FEDA75 0%, #FA7E1E 25%, #D62976 50%, #962FBF 75%, #4F5BD5 100%)' }}
            >
                <InstagramIcon size={iconPx} title="" />
            </a>
            <a
                href={SOCIAL.facebook.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('social.ariaFacebook')}
                onClick={() => trackClick('facebook')}
                className={`${base} bg-[#1877F2] text-white hover:bg-[#0f63d6]`}
            >
                <FacebookIcon size={iconPx} title="" />
            </a>
        </div>
    );
}

export default SocialLinks;
